import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

/**
 * Haptic Feedback Util (iOS + Android via Capacitor)
 *
 * Gibt es nur auf nativen Plattformen aus – im Browser still no-op.
 * Kostet praktisch keine Performance (kurzer Bridge-Call, fire-and-forget).
 *
 * Verwendung:
 *   import triggerHaptic, { haptic } from '../utils/haptics';
 *   triggerHaptic('success');   // String-API (rückwärtskompatibel)
 *   haptic.success();           // Semantische API (bevorzugt)
 *   haptic.selection();         // z.B. beim Scrubben/Toggeln (iOS-Picker-Gefühl)
 *   haptic.match();             // Komponierter "Moment" (Doppel-Tap)
 */

const isNative = () => Capacitor.isNativePlatform();

const impact = async (style) => {
  if (!isNative()) return;
  try {
    await Haptics.impact({ style });
  } catch (e) {
    // Haptics sind nie kritisch – still scheitern
  }
};

const notify = async (type) => {
  if (!isNative()) return;
  try {
    await Haptics.notification({ type });
  } catch (e) {
    // still scheitern
  }
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Dauer des Android-Ticks fuer haptic.selection() (siehe dort).
const ANDROID_TICK_MS = 12;

/**
 * Semantische Haptik-API.
 * Einzel-Pulse fühlen sich nativ an, "Momente" (match/levelUp/error) sind
 * kurze, bewusst getimte Sequenzen für emotionale Highlights.
 */
export const haptic = {
  // --- Einfache Impulse ---
  light: () => impact(ImpactStyle.Light),
  medium: () => impact(ImpactStyle.Medium),
  heavy: () => impact(ImpactStyle.Heavy),

  // iOS-"Selection"-Feedback: ideal beim Durchscrubben von Auswahl-Rastern,
  // Togglen von Zellen, Steppern.
  //
  // Android (Oktober 2026): Das Plugin macht aus selectionChanged eine
  // Wellenform von 100ms (HapticsSelectionType.java) — doppelt so lang wie
  // ein "leichter" Impuls. Beim Durchwischen der Karten war das bei jeder
  // Karte ein Summen statt eines Ticks, und es brauchte drei Bridge-Aufrufe
  // hintereinander. Ein einzelner 12ms-Puls kommt dem iOS-Tick am naechsten.
  selection: async () => {
    if (!isNative()) return;
    if (Capacitor.getPlatform?.() === 'android') {
      try {
        await Haptics.vibrate({ duration: ANDROID_TICK_MS });
      } catch (e) {
        // still scheitern
      }
      return;
    }
    try {
      await Haptics.selectionStart();
      await Haptics.selectionChanged();
      await Haptics.selectionEnd();
    } catch (e) {
      // Fallback auf leichten Impact
      impact(ImpactStyle.Light);
    }
  },

  // --- System-Notifications ---
  success: () => notify(NotificationType.Success),
  warning: () => notify(NotificationType.Warning),
  error: () => notify(NotificationType.Error),

  // --- Komponierte "Momente" ---
  // Match gefunden: zwei kräftige Schläge wie ein Herzschlag.
  match: async () => {
    if (!isNative()) return;
    await impact(ImpactStyle.Medium);
    await wait(110);
    await impact(ImpactStyle.Heavy);
  },

  // Level-Up / Aura: aufsteigendes Crescendo + Erfolgs-Bestätigung.
  levelUp: async () => {
    if (!isNative()) return;
    await impact(ImpactStyle.Light);
    await wait(90);
    await impact(ImpactStyle.Medium);
    await wait(90);
    await notify(NotificationType.Success);
  },

  // Card-Flip: kurzer "Snap" – leichter Anlauf, dann satter Aufprall, wie ein
  // umschlagendes Kärtchen. Für das Umdrehen von Karten (z.B. ProfileCard).
  flip: async () => {
    if (!isNative()) return;
    await impact(ImpactStyle.Light);
    await wait(55);
    await impact(ImpactStyle.Medium);
  }
};

/**
 * Rückwärtskompatible Default-Funktion.
 * Akzeptiert alle Keys aus `haptic` als String.
 */
const triggerHaptic = (type = 'light') => {
  const fn = haptic[type] || haptic.light;
  return fn();
};

export default triggerHaptic;
